import { useRef, useState, useCallback, useEffect, type ReactNode, type MouseEvent } from 'react';

// Sync check to avoid flash on first render
function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return true;
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    window.matchMedia('(hover: none) and (pointer: coarse)').matches ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

interface TiltCardProps {
  children: ReactNode;
  className?: string;
  maxTilt?: number;
  scale?: number;
  speed?: number;
  glare?: boolean;
}

export default function TiltCard({
  children,
  className = '',
  maxTilt = 8,
  scale = 1.02,
  speed = 400,
  glare = true,
}: TiltCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);
  const targetRef = useRef({ rx: 0, ry: 0, gx: 50, gy: 50 });
  const [style, setStyle] = useState({ transform: '', transition: '' });
  const [glareStyle, setGlareStyle] = useState({ opacity: 0, backgroundPosition: '0% 0%' });
  // Use lazy init to avoid flash on touch devices
  const [isTouch] = useState(() => isTouchDevice());

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent<HTMLDivElement>) => {
    if (isTouch) return;
    const card = cardRef.current;
    if (!card) return;

    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    targetRef.current.rx = ((y - centerY) / centerY) * -maxTilt;
    targetRef.current.ry = ((x - centerX) / centerX) * maxTilt;
    targetRef.current.gx = (x / rect.width) * 100;
    targetRef.current.gy = (y / rect.height) * 100;
  }, [maxTilt, isTouch]);

  const handleMouseEnter = useCallback(() => {
    if (isTouch) return;
    let currentRx = 0;
    let currentRy = 0;
    let currentGx = 50;
    let currentGy = 50;

    const tick = () => {
      const t = targetRef.current;
      const lerp = 0.22;
      currentRx += (t.rx - currentRx) * lerp;
      currentRy += (t.ry - currentRy) * lerp;
      currentGx += (t.gx - currentGx) * lerp;
      currentGy += (t.gy - currentGy) * lerp;

      setStyle({
        transform: `perspective(800px) rotateX(${currentRx}deg) rotateY(${currentRy}deg) scale3d(${scale}, ${scale}, ${scale})`,
        transition: 'none',
      });

      if (glare) {
        setGlareStyle({
          opacity: 0.12,
          backgroundPosition: `${currentGx}% ${currentGy}%`,
        });
      }

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [scale, glare, isTouch]);

  const handleMouseLeave = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    targetRef.current = { rx: 0, ry: 0, gx: 50, gy: 50 };
    setStyle({
      transform: 'perspective(800px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)',
      transition: `transform ${speed}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`,
    });
    setGlareStyle({ opacity: 0, backgroundPosition: '0% 0%' });
  }, [speed]);

  if (isTouch) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div
      ref={cardRef}
      className={`relative overflow-hidden ${className}`}
      style={{
        ...style,
        willChange: 'transform',
      }}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {glare && (
        <div
          className="absolute inset-0 pointer-events-none z-10"
          style={{
            background: 'radial-gradient(circle at center, rgba(255,255,255,0.5) 0%, transparent 70%)',
            opacity: glareStyle.opacity,
            backgroundPosition: glareStyle.backgroundPosition,
            backgroundRepeat: 'no-repeat',
            backgroundSize: '200% 200%',
            transition: 'opacity 0.3s ease',
          }}
        />
      )}
      {children}
    </div>
  );
}